import { generateClient } from '@aws-amplify/api';
import {
  signIn as amplifySignIn,
  confirmSignIn as amplifyConfirmSignIn,
  signOut as amplifySignOut,
  fetchAuthSession,
  getCurrentUser,
  signInWithRedirect,
  resetPassword,
  confirmResetPassword,
  confirmSignUp,
  signUp,
  fetchUserAttributes,
  updateUserAttribute as amplifyUpdateUserAttribute,
  updateMFAPreference as amplifyUpdateMFAPreference,
  fetchMFAPreference as amplifyFetchMFAPreference,
  type SignInWithRedirectInput,
} from '@aws-amplify/auth';

import { configureAmplify } from './amplifyConfig';
import { getApiGatewayUrl } from './environment';
import { useLogger } from '../hooks/useLogger';

export interface SignInOutput {
  isSignedIn: boolean;
  nextStep?: {
    signInStep: string;
    codeDeliveryDetails?: {
      deliveryMedium: string;
      destination: string;
      attributeName?: string;
    };
  };
}

const logger = useLogger('amplifyBootstrap');

let isAmplifyInitialized = false;
let apiClient = generateClient();

export function initializeAmplify() {
  if (isAmplifyInitialized) {
    logger.warn('Amplify already initialized');
    return apiClient;
  }

  configureAmplify();
  isAmplifyInitialized = true;
  // @ts-ignore
  apiClient = generateClient();
  return apiClient;
}

initializeAmplify();
const AuthModule = {
  signInWithRedirect,

  signIn: async (params: any) => amplifySignIn(params),

  confirmSignIn: async (params: any) => amplifyConfirmSignIn(params),

  signOut: async () => {
    try {
      return await amplifySignOut();
    } catch (error) {
      logger.error('Error in signOut:', { error });
      throw error;
    }
  },

  currentAuthenticatedUser: async () => {
    try {
      const user = await getCurrentUser();
      return user;
    } catch (error) {
      logger.error('Error in currentAuthenticatedUser:', { error });
      throw error;
    }
  },

  currentSession: async (forceRefresh?: boolean) => {
    try {
      const session = await fetchAuthSession({ forceRefresh });
      return session;
    } catch (error) {
      logger.error('Error in currentSession:', { error });
      throw error;
    }
  },

  fetchUserAttributes: async () => {
    try {
      const attributes = await fetchUserAttributes();
      return attributes;
    } catch (error) {
      logger.error('Error in fetchUserAttributes:', { error });
      throw error;
    }
  },
};

export interface AuthInterface {
  signIn: (username: string, password: string) => Promise<SignInOutput>;
  confirmSignIn: (params: {
    challengeResponse: string;
    options?: {
      userAttributes?: Record<string, string>;
    };
  }) => Promise<any>;
  signOut: () => Promise<any>;
  currentAuthenticatedUser: () => Promise<any>;
  currentSession: (forceRefresh?: boolean) => Promise<any>;
  signInWithRedirect: (input?: SignInWithRedirectInput) => Promise<void>;
  signUp: (username: string, password: string, attributes: Record<string, string>) => Promise<any>;
  confirmSignUp: (username: string, code: string) => Promise<any>;
  forgotPassword: (username: string) => Promise<any>;
  forgotPasswordSubmit: (username: string, code: string, newPassword: string) => Promise<any>;
  fetchUserAttributes: () => Promise<Record<string, string | undefined>>;
  updateUserAttribute: (key: string, value: string) => Promise<any>;
  disableTOTP: () => Promise<void>;
  fetchMFAStatus: () => Promise<MFAStatus>;

  getPreferredMFA: (user: any) => Promise<string>;
  setPreferredMFA: (user: any, mfaMethod: string) => Promise<any>;
}

export type MFAStatus = 'TOTP' | 'NOMFA';

const MFAModule = {
  getPreferredMFA: async (_user: any): Promise<string> => {
    try {
      const pref = await amplifyFetchMFAPreference();
      if (pref.preferred === 'TOTP' || pref.enabled?.includes('TOTP')) return 'TOTP';
      return 'NOMFA';
    } catch (error) {
      logger.error('Error in getPreferredMFA:', { error });
      return 'NOMFA';
    }
  },

  setPreferredMFA: async (user: any, mfaMethod: string): Promise<any> => {
    try {
      if (user && typeof user.setPreferredMFA === 'function') {
        return await user.setPreferredMFA(mfaMethod);
      }

      return { success: false, message: 'MFA preference setting not implemented' };
    } catch (error) {
      logger.error('Error setting preferred MFA:', { error });
      throw error;
    }
  },
};

export const Auth: AuthInterface = {
  signInWithRedirect: AuthModule.signInWithRedirect,

  fetchUserAttributes: AuthModule.fetchUserAttributes,

  updateUserAttribute: async (key: string, value: string) => {
    try {
      const result = await amplifyUpdateUserAttribute({
        userAttribute: { attributeKey: key, value },
      });
      return result;
    } catch (error) {
      logger.error('Error in updateUserAttribute:', { error });
      throw error;
    }
  },

  disableTOTP: async () => {
    try {
      await amplifyUpdateMFAPreference({
        totp: 'DISABLED',
      });
    } catch (error) {
      logger.error('Error in disableTOTP:', { error });
      throw error;
    }
  },

  fetchMFAStatus: async (): Promise<MFAStatus> => {
    try {
      const pref = await amplifyFetchMFAPreference();
      if (pref.preferred === 'TOTP' || pref.enabled?.includes('TOTP')) return 'TOTP';
      return 'NOMFA';
    } catch (error) {
      logger.error('Error in fetchMFAStatus:', { error });
      return 'NOMFA';
    }
  },

  signIn: async (username: string, password: string) => {
    try {
      const signInOutput = await amplifySignIn({
        username,
        password,
      });

      const result: SignInOutput = {
        isSignedIn: signInOutput.isSignedIn,
      };

      if (signInOutput.nextStep) {
        result.nextStep = {
          signInStep: signInOutput.nextStep.signInStep,
        };

        const codeDetails = (signInOutput.nextStep as any).codeDeliveryDetails;
        if (codeDetails) {
          result.nextStep.codeDeliveryDetails = {
            deliveryMedium: codeDetails.deliveryMedium,
            destination: codeDetails.destination,
            attributeName: codeDetails.attributeName,
          };
        }
      }

      return result;
    } catch (err) {
      logger.error('Error in signIn:', { err });
      const error = err instanceof Error ? err : new Error(String(err));
      throw error;
    }
  },

  confirmSignIn: async (params: {
    challengeResponse: string;
    options?: {
      userAttributes?: Record<string, string>;
    };
  }) => {
    try {
      const result = await amplifyConfirmSignIn(params);
      return result;
    } catch (error) {
      logger.error('Error in confirmSignIn:', { error });
      throw error;
    }
  },

  signOut: AuthModule.signOut,

  currentAuthenticatedUser: AuthModule.currentAuthenticatedUser,

  currentSession: AuthModule.currentSession,

  signUp: async (username: string, password: string, attributes: Record<string, string>) => {
    try {
      const result = await signUp({
        username,
        password,
        options: {
          userAttributes: attributes,
        },
      });
      return result;
    } catch (error) {
      logger.error('Error in signUp:', { error });
      throw error;
    }
  },

  confirmSignUp: async (username: string, code: string) => {
    try {
      const result = await confirmSignUp({
        username,
        confirmationCode: code,
      });
      return result;
    } catch (error) {
      logger.error('Error in confirmSignUp:', { error });
      throw error;
    }
  },

  forgotPassword: async (username: string) => {
    try {
      const result = await resetPassword({
        username,
      });
      return result;
    } catch (error) {
      logger.error('Error in forgotPassword:', { error });
      throw error;
    }
  },

  forgotPasswordSubmit: async (username: string, code: string, newPassword: string) => {
    try {
      const result = await confirmResetPassword({
        username,
        confirmationCode: code,
        newPassword,
      });
      return result;
    } catch (error) {
      logger.error('Error in forgotPasswordSubmit:', { error });
      throw error;
    }
  },

  getPreferredMFA: MFAModule.getPreferredMFA,
  setPreferredMFA: MFAModule.setPreferredMFA,
};

interface ExtendedAPI {
  graphql: typeof apiClient.graphql;
  post: (path: string, init?: any) => Promise<any>;
  get: (path: string, init?: any) => Promise<any>;
}

const API = Object.assign({}, apiClient, {
  post: async (path: string, init?: any) => {
    const apiGatewayUrl = getApiGatewayUrl();

    const fullUrl = apiGatewayUrl
      ? path.startsWith('/')
        ? `${apiGatewayUrl}${path}`
        : `${apiGatewayUrl}/${path}`
      : path;

    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...init?.headers,
      },
      body: JSON.stringify(init?.body),
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response.json();
  },
  get: async (path: string, init?: any) => {
    const apiGatewayUrl = getApiGatewayUrl();

    const fullUrl = apiGatewayUrl
      ? path.startsWith('/')
        ? `${apiGatewayUrl}${path}`
        : `${apiGatewayUrl}/${path}`
      : path;

    logger.debug(`Making API GET request to: ${fullUrl}`);

    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...init?.headers,
      },
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response.json();
  },
}) as ExtendedAPI;

export { API };

export const graphqlOperation = (query: any, variables?: any) => ({
  query,
  variables,
});

export default apiClient;
